import { useState } from 'react';
import {
  useCreateLogEntry, useCreateTask, usePatchTask,
  type IncidentLogEntry, type IncidentTask,
} from '../api';
import RelativeTime from './RelativeTime';

const ENTRY_TYPES: IncidentLogEntry['entry_type'][] = ['note', 'radio', 'message'];
const PRIORITY_TONE: Record<IncidentTask['priority'], string> = {
  life_safety: 'bg-rose-600 text-white',
  urgent: 'bg-amber-500 text-white',
  routine: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
};

/**
 * Open work items above, immutable chronology below.
 *
 * They are separate on purpose: a task gets closed, an activity log does not
 * get edited. Closing a task appends a log line, so the mutable list still
 * leaves a permanent trace.
 */
export default function LogRail({ incidentId, tasks, entries }: {
  incidentId: string;
  tasks: IncidentTask[];
  entries: IncidentLogEntry[];
}) {
  return (
    <div className="space-y-3">
      <Tasks incidentId={incidentId} tasks={tasks} />
      <Log incidentId={incidentId} entries={entries} />
    </div>
  );
}

function Tasks({ incidentId, tasks }: { incidentId: string; tasks: IncidentTask[] }) {
  const create = useCreateTask();
  const patch = usePatchTask();
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<IncidentTask['priority']>('routine');

  return (
    <section className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2">
      <h3 className="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Open items {tasks.length > 0 && <span className="tabular-nums">({tasks.length})</span>}
      </h3>

      {tasks.length === 0 && <p className="text-xs italic text-slate-400 dark:text-slate-500">Nothing outstanding.</p>}
      <ul className="space-y-1">
        {tasks.map((t) => (
          <li key={t.id} className="flex items-start gap-1.5 text-xs">
            <button
              onClick={() => patch.mutate({ incidentId, id: t.id, body: { status: 'done' } })}
              title="Mark done"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border border-slate-400 dark:border-slate-500 hover:bg-emerald-500 hover:border-emerald-500"
            />
            <span className="flex-1 text-slate-700 dark:text-slate-200">{t.body}</span>
            {t.priority !== 'routine' && (
              <span className={`shrink-0 rounded px-1 ${PRIORITY_TONE[t.priority]}`}>
                {t.priority === 'life_safety' ? 'LIFE SAFETY' : 'URGENT'}
              </span>
            )}
          </li>
        ))}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim()) return;
          create.mutate({ incidentId, body: body.trim(), priority },
            { onSuccess: () => { setBody(''); setPriority('routine'); } });
        }}
        className="mt-2 flex gap-1"
      >
        <input
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Add an item to work"
          className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        />
        <select
          value={priority} onChange={(e) => setPriority(e.target.value as IncidentTask['priority'])}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-xs"
        >
          <option value="routine">Routine</option>
          <option value="urgent">Urgent</option>
          <option value="life_safety">Life safety</option>
        </select>
      </form>
    </section>
  );
}

function Log({ incidentId, entries }: { incidentId: string; entries: IncidentLogEntry[] }) {
  const create = useCreateLogEntry();
  const [body, setBody] = useState('');
  const [type, setType] = useState<IncidentLogEntry['entry_type']>('note');
  const [when, setWhen] = useState('');

  return (
    <section className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2">
      <h3 className="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Activity log</h3>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim()) return;
          create.mutate(
            { incidentId, body: body.trim(), entry_type: type,
              occurred_at: when ? new Date(when).toISOString() : null },
            { onSuccess: () => { setBody(''); setWhen(''); } },
          );
        }}
        className="mb-2 space-y-1"
      >
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          rows={2} placeholder="Radio traffic, a decision, a message to pass on…"
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-1 text-xs"
        />
        <div className="flex gap-1">
          <select
            value={type} onChange={(e) => setType(e.target.value as IncidentLogEntry['entry_type'])}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-xs"
          >
            {ENTRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {/* Backdating matters: a runner returns at 21:40 with checks made at
              21:05. Recording when it happened, not when it was typed, is what
              makes the chronology worth printing. */}
          <input
            type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
            title="When it happened (leave blank for now)"
            className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-xs"
          />
          <button type="submit" disabled={create.isPending || !body.trim()}
            className="rounded bg-slate-800 dark:bg-slate-200 px-2 text-xs text-white dark:text-slate-900 disabled:opacity-40">
            Log
          </button>
        </div>
      </form>

      <ul className="max-h-96 space-y-1.5 overflow-y-auto">
        {entries.map((e) => (
          <li key={e.id} className="text-xs">
            <div className="flex items-baseline gap-1.5">
              <RelativeTime at={e.occurred_at} />
              <span className="rounded bg-slate-100 dark:bg-slate-800 px-1 text-slate-500 dark:text-slate-400">
                {e.entry_type}
              </span>
            </div>
            <p className="text-slate-700 dark:text-slate-200">{e.body}</p>
          </li>
        ))}
        {entries.length === 0 && <li className="text-xs italic text-slate-400">Nothing logged yet.</li>}
      </ul>
    </section>
  );
}
